import { useAuth0 } from "@auth0/auth0-react";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { AppErrorFallback } from "@/components/AppErrorFallback";
import LogOutButton from "@/components/common/Buttons/LogOutButton";
import ErrorPopup from "@/components/common/Popups/ErrorPopup";
import Popup from "@/components/common/Popups/Popup";
import LoadingDots from "@/components/LoadingDots";
import Navbar from "@/components/Navbar";
import Timer from "@/components/Timer";
import useInitialization from "@/hooks/useInitialization";
import { useNavigationListener } from "@/hooks/useNavigationListener";
import { useSessionValidation } from "@/hooks/useSessionValidation";
import { CameraDebugBadge, getCameraProvider, useNativeCameraSlotActive } from "@/lib/camera";
import logger from "@/lib/logging/logger";
import { useTestNativeConnection } from "@/lib/native-bridge";
import { visionSearchParamsSchema } from "@/routes/-search-params";
import { platformApi, registerGetAccessToken } from "@/services";
import { useStore } from "@/store";
import { getAuth0LoginOptions, getAuth0UserLocationId, getAuth0UserPartnerCode } from "@/utils/auth0";
import { withTimeout } from "@/utils/helpers";
import { ensurePartnerConfig } from "@/utils/partnerConfig";
import { resolveLocationId } from "@/utils/resolveLocationId";
import "@/utils/monitorGlobalState";
import { useEffect, useRef } from "react";
import { AlertCircle } from "tabler-icons-react";

// Above the SDK's own renewal budget (a 5 s lock wait, then three 10 s attempts), so the
// bound fires only when renewal genuinely never settles; past it, a full re-login redirect.
const TOKEN_RENEWAL_TIMEOUT_MS = 40_000;

export const Route = createFileRoute("/$locale/$partner/_app")({
	validateSearch: (search) => visionSearchParamsSchema.parse(search),
	// Per-partner login gate. On `_app` (not `$partner`) so the sibling `peer` route
	// stays reachable without a session. Config is already loaded by the parent loader.
	loaderDeps: ({ search }) => ({ locationId: search.locationId }),
	loader: async ({ params, context, deps }) => {
		// Await the config directly rather than reading the parent loader's store side
		// effect — nested loaders run in parallel, so it may not have landed yet.
		const locationId = resolveLocationId(deps.locationId, context.auth.user);
		const { authentication } = await ensurePartnerConfig(params.partner, locationId);
		if (!authentication?.requiresAuthentication) return;

		// Route to this partner's own Auth0 connection when configured (e.g. Carrefour),
		// overriding the host-based default. Falls back to the host default when absent.
		const baseLoginOptions = getAuth0LoginOptions(window.location);
		const loginOptions = authentication.connection
			? { ...baseLoginOptions, authorizationParams: { connection: authentication.connection } }
			: baseLoginOptions;

		if (!context.auth.isAuthenticated) {
			logger.info("FLOW", "No active session in _app loader; redirecting to Auth0 login", {
				partner: params.partner,
				locale: params.locale,
			});
			context.auth.loginWithRedirect(loginOptions);
			// Hold the router pending while Auth0 navigates away (as in the root route).
			return new Promise<never>(() => {});
		}

		// `isAuthenticated` only means a cached session record exists — it does NOT guarantee
		// a usable token. If silent renewal fails (dead/revoked refresh token, ended Auth0
		// session), force a real re-login instead of letting the app proceed and create
		// sessions with a null user. A renewal that never settles rejects into the same
		// catch past the bound, instead of holding the router pending on engines whose
		// silent auth stalls. The log lets us trace this cohort post-hotfix.
		try {
			await withTimeout(
				context.auth.getAccessTokenSilently(),
				TOKEN_RENEWAL_TIMEOUT_MS,
				"Silent token renewal timed out",
			);
		} catch (error) {
			const errorCode = error && typeof error === "object" && "error" in error ? String(error.error) : null;
			logger.warning("FLOW", "Active session but silent token acquisition failed in _app loader; forcing re-login", {
				partner: params.partner,
				locale: params.locale,
				errorCode,
				reason: error instanceof Error ? error.message : String(error),
			});
			context.auth.loginWithRedirect(loginOptions);
			return new Promise<never>(() => {});
		}

		// Authenticated: the user must belong to this partner. partner_code claim first,
		// then derive from the location_id claim (mirrors the root route).
		let userPartner = getAuth0UserPartnerCode(context.auth.user);
		if (!userPartner) {
			const locationId = getAuth0UserLocationId(context.auth.user);
			if (locationId) {
				try {
					const locationData = await platformApi.getLocationAndPartner(locationId);
					userPartner = locationData.Partner.code;
				} catch {
					// fall through to access_denied
				}
			}
		}

		if (userPartner !== params.partner) {
			throw redirect({
				to: "/$locale/error",
				params: { locale: params.locale },
				search: { code: "access_denied" },
				replace: true,
			});
		}
	},
	errorComponent: AppErrorFallback,
	component: InitializedLayout,
});

function InitializedLayout() {
	const themeMode = useStore((state) => state.themeMode);
	const setThemeMode = useStore((state) => state.setThemeMode);
	const resetTimer = useStore((state) => state.resetTimer);
	const error = useStore((state) => state.error);
	const setError = useStore((state) => state.setError);
	const { getAccessTokenSilently, isAuthenticated, isLoading: isAuthLoading } = useAuth0();
	const wasAuthenticatedRef = useRef(false);
	const { isLoading, isValidConfiguration } = useInitialization();
	const nativeCameraSlotActive = useNativeCameraSlotActive();
	// A fading background would show the wrapper's preview layer through the page while it runs.
	const backgroundTransition = getCameraProvider() === "native" ? "" : "transition-all duration-500 ease-in-out";

	// Register Auth0 access token getter lazily — not in beforeLoad which
	// blocks route processing. Only a signed-in session registers one: a QR-entry
	// session has no token to acquire, and asking Auth0 anyway stalls on WebViews
	// whose silent-auth attempt never settles. A session that LOSES its sign-in
	// mid-flight (the SDK logs itself out on a dead refresh token) is not that
	// case: it stays loud, in one warning instead of one per request.
	useEffect(() => {
		if (isAuthLoading) return;
		registerGetAccessToken(isAuthenticated ? getAccessTokenSilently : null);
		if (isAuthenticated) {
			wasAuthenticatedRef.current = true;
			return;
		}
		if (wasAuthenticatedRef.current) {
			logger.warning("NETWORK", "Signed-in session lost its Auth0 session; requests continue unauthenticated");
			return;
		}
		logger.info("NETWORK", "No signed-in session; platform requests are sent unauthenticated");
	}, [isAuthLoading, isAuthenticated, getAccessTokenSilently]);

	useTestNativeConnection((msg) =>
		logger.debug("FLOW", "Established native connection response", typeof msg === "string" ? { reply: msg } : msg),
	);
	useNavigationListener(!isLoading && isValidConfiguration);
	const { canRenderChildren } = useSessionValidation(!isLoading && isValidConfiguration);

	useEffect(() => {
		setThemeMode("light");
	}, [setThemeMode]);

	if (isLoading) {
		return (
			<div className="flex h-[100dvh] items-center justify-center">
				<LoadingDots />
			</div>
		);
	}

	if (!isValidConfiguration)
		return (
			<>
				<Popup
					show={true}
					content={
						<div className="flex flex-col items-center">
							<AlertCircle size={64} />
							<h2 className="font-secondary mt-8">Invalid configuration</h2>
							<p>Please contact support</p>
						</div>
					}
				/>
				<div className="absolute top-2 right-2 z-[60]">
					<LogOutButton />
				</div>
			</>
		);

	return (
		<div
			id="layout"
			className={`h-[100dvh] w-[100dvw] overflow-hidden flex flex-col ${
				nativeCameraSlotActive ? "bg-transparent" : themeMode === "dark" ? "bg-zinc-900" : "bg-zinc-100"
			} ${backgroundTransition} sm:px-10 sm:pb-10`}
			onClick={resetTimer}
		>
			<Navbar />
			{canRenderChildren ? <Outlet /> : null}
			{error !== null && (
				<ErrorPopup
					show={true}
					theme={themeMode}
					error={error}
					setError={setError}
				/>
			)}
			<Timer />
			<CameraDebugBadge />
		</div>
	);
}
